import React, { useEffect, useState } from "react";
import {
    Select,
    Button,
    Row,
    Typography,
    message,
    Card,
    Space,
    Divider,
    Segmented,
    Tooltip
} from "antd";

import { useDispatch, useSelector } from "react-redux";
import { audioMessage } from "./Notifications/audioMessage";
import { SelectWithAdd } from "./SelectWithAdd";
import { useAuth0 } from "@auth0/auth0-react";
import { trainingAPI } from "../../api/TrainingAPI";
import { setTeamPresets } from "../../store/actions/team.actions";
import { IGeneralReportsConfig, IReportTypes } from "../Report/constants";
import { getChartType } from "../../Utils/chart_utils";
import { useMetricDropdowns } from "../../hooks/useDropdownInitialization";


const { Option } = Select;
const { Title, Text } = Typography;

const getEmptySelections = (count: number) => {
    let res = [];
    for (let i = 0; i < count; i++) {
        res.push({ 'metric': undefined, 'agg': 'avg' });
    }
    return res;
}

export const MultiMetricPicker = ({ requirements, setResult, setOpen, showAggs = false, noAsym = true, presetInfo, config, metricSelectorPrefix }: {
    showAggs: boolean,
    noAsym: boolean,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    setResult: (x: any) => void,
    requirements?: IReportTypes,
    presetInfo?: {
        items: { name: string, config: any[], type: string, creator_id__email: string, preset_id: number }[]
    },
    config?: IGeneralReportsConfig,
    metricSelectorPrefix?: string
}) => {
    const dispatch = useDispatch();
    const {
        positions,
        dropdowns: { forceplate: forceplateDropdowns,
            testing: testingDropdowns,
            gps: gpsDropdowns,
        },
        playerGroups: groups,
        allPlayers,
        all_metric_types,
        info: { team_colors: teamColors },
    } = useSelector(({ team }) => team);

    const { user } = useAuth0();
    useMetricDropdowns(['forceplate', 'testing', 'gps']);
    showAggs = showAggs || requirements?.metric_selector_info?.useAggs || false;

    const [metricDropdown, setMetricDropdown] = useState<any[]>([]);
    const [unsavedPresetChanges, setUnsavedPresetChanges] = useState<boolean>(false)

    const formatNumber = (n: any) => n.toString().padStart(2, '0');

    const presetType = requirements?.preset?.find((e) => e.key === config?.current_modal_type)?.presetType

    const presetItemsOfInterest = presetInfo?.items.filter((e) => e.type === presetType)
    const presetItems = presetItemsOfInterest?.map((preset) => {
        return {
            label: preset.name,
            value: preset.name,
            disabled: preset.creator_id__email !== user?.email,
        }
    })
    const [currentSelectedTilePreset, setCurrentSelectedTilePreset] = useState<string | undefined>(undefined)
    const getPresetSelected = () => presetItemsOfInterest?.find((preset) => preset.name === currentSelectedTilePreset)
    const configSelections = (config as any)[`${metricSelectorPrefix}-metrics`]
    const [selections, setSelections] = useState<{ 'metric'?: string, 'agg': string }[]>(
        JSON.parse(JSON.stringify(
            getPresetSelected()?.config ||
            configSelections ||
            getEmptySelections(requirements?.metric_selector_info?.count || 2)
        )));

    const addPreset = async (item: string) => {
        const newPreset = await trainingAPI.createPreset({ name: item, type: presetType, config: selections });
        dispatch(setTeamPresets(newPreset))

        setCurrentSelectedTilePreset(item)
    }

    const deletePreset = async (item: string) => {
        const presetOfInterest = presetItemsOfInterest?.find((preset) => preset.name === item)
        await trainingAPI.deletePreset(presetOfInterest?.preset_id)

        const updatedPreset = presetInfo?.items.filter(preset => preset.preset_id !== presetOfInterest?.preset_id)
        dispatch(setTeamPresets(updatedPreset))

        setCurrentSelectedTilePreset((presetItems && presetItems?.length > 0) ? presetItems[0].value : undefined)
    }

    const updatedPreset = async () => {
        const presetModified = presetItemsOfInterest?.find((preset) => preset.name === currentSelectedTilePreset)
        await trainingAPI.updatePreset({
            name: currentSelectedTilePreset, type: presetType, config: selections, preset_id: presetModified?.preset_id
        });
        dispatch(setTeamPresets(presetInfo?.items))

        setUnsavedPresetChanges(false)
        message.success("Saved!");
    }

    const getTooltipText = () => {
        if (getPresetSelected()?.creator_id__email !== user?.email) {
            return 'Cannot modify tiles created by another user'
        }

        if (unsavedPresetChanges) {
            return 'You have unsaved changes'
        }

        return undefined
    }

    useEffect(() => {
        if (currentSelectedTilePreset) {
            setSelections(JSON.parse(JSON.stringify(
                getPresetSelected()?.config ||
                configSelections ||
                getEmptySelections(requirements?.metric_selector_info?.count || 2)
            )))
        }
    }, [currentSelectedTilePreset])

    useEffect(() => {
        if (presetItems && presetItems.length === 0) {
            setCurrentSelectedTilePreset(undefined)
        }
    }, [presetItems])

    useEffect(() => {
        let optGroupDropdown: { displayLabel: string, key: string, label: string, options: any[] }[] = [];

        // Deep copy all_metric_types and filter all arrays based on dropdowns
        const filteredMetricTypes = JSON.parse(JSON.stringify(all_metric_types || {}));

        // Create lookup maps for dropdown labels
        const gpsLabelMap = new Map();
        const forceplateLabelMap = new Map();
        const testingLabelMap = new Map();

        // Build GPS label map
        if (gpsDropdowns?.length > 0) {
            gpsDropdowns.forEach((dropdownItem: any) => {
                if (dropdownItem.children) {
                    dropdownItem.children.forEach((child: any) => {
                        gpsLabelMap.set(child.key, child.label);
                    });
                } else {
                    gpsLabelMap.set(dropdownItem.key, dropdownItem.label);
                }
            });
        }

        // Build forceplate label map
        if (forceplateDropdowns?.length > 0) {
            forceplateDropdowns.forEach((dropdownItem: any) => {
                if (dropdownItem.children) {
                    dropdownItem.children.forEach((child: any) => {
                        forceplateLabelMap.set(child.key, dropdownItem.label + " - " + child.label);
                    });
                } else {
                    forceplateLabelMap.set(dropdownItem.key, dropdownItem.label);
                }
            });
        }

        // Build testing label map
        if (testingDropdowns?.length > 0) {
            testingDropdowns.forEach((dropdownItem: any) => {
                if (dropdownItem.children) {
                    dropdownItem.children.forEach((child: any) => {
                        testingLabelMap.set(child.key, dropdownItem.label + " - " + child.label);
                    });
                } else {
                    testingLabelMap.set(dropdownItem.key, dropdownItem.label);
                }
            });
        }

        // Filter GPS metrics based on gpsDropdowns
        if (filteredMetricTypes.gps && gpsDropdowns?.length > 0) {
            const gpsDropdownKeys = new Set();

            // Extract all unique keys from gpsDropdowns (including nested children)
            gpsDropdowns.forEach((dropdownItem: any) => {
                if (dropdownItem.children) {
                    dropdownItem.children.forEach((child: any) => {
                        gpsDropdownKeys.add(child.key);
                    });
                } else {
                    gpsDropdownKeys.add(dropdownItem.key);
                }
            });

            // Filter GPS metrics to only include those in the dropdowns
            filteredMetricTypes.gps = filteredMetricTypes.gps.filter((metric: any) =>
                gpsDropdownKeys.has(metric.unique_identifier)
            );
        }

        // Filter forceplate metrics based on forceplateDropdowns
        if (filteredMetricTypes.forceplate && forceplateDropdowns?.length > 0) {
            const forceplateDropdownKeys = new Set();

            // Extract all unique keys from forceplateDropdowns (including nested children)
            forceplateDropdowns.forEach((dropdownItem: any) => {
                if (dropdownItem.children) {
                    dropdownItem.children.forEach((child: any) => {
                        forceplateDropdownKeys.add(child.key);
                    });
                } else {
                    forceplateDropdownKeys.add(dropdownItem.key);
                }
            });

            // Filter forceplate metrics to only include those in the dropdowns
            filteredMetricTypes.forceplate = filteredMetricTypes.forceplate.filter((metric: any) =>
                forceplateDropdownKeys.has(metric.unique_identifier)
            );
        }

        // Filter testing metrics based on testingDropdowns
        if (filteredMetricTypes.testing && testingDropdowns?.length > 0) {
            const testingDropdownKeys = new Set();

            // Extract all unique keys from testingDropdowns (including nested children)
            testingDropdowns.forEach((dropdownItem: any) => {
                if (dropdownItem.children) {
                    dropdownItem.children.forEach((child: any) => {
                        testingDropdownKeys.add(child.key);
                    });
                } else {
                    testingDropdownKeys.add(dropdownItem.key);
                }
            });

            // Filter testing metrics to only include those in the dropdowns
            filteredMetricTypes.testing = filteredMetricTypes.testing.filter((metric: any) =>
                testingDropdownKeys.has(metric.unique_identifier)
            );
        }

        requirements?.metric_selector_info?.types?.forEach((metric_type: any) => {
            let groupLabel;
            if (metric_type === "gps") groupLabel = "GPS";
            else groupLabel = metric_type.charAt(0).toUpperCase() + metric_type.slice(1).toLowerCase(); //Capitalize

            let groupOptions: any[] = [];
            (filteredMetricTypes?.[metric_type] || [])?.forEach((metric: any) => {
                const { unique_identifier, name } = metric;

                // Use dropdown label for all metric types, fallback to metric name
                let displayLabel = name;
                if (metric_type === 'gps' && gpsLabelMap.has(unique_identifier)) {
                    displayLabel = gpsLabelMap.get(unique_identifier);
                } else if (metric_type === 'forceplate' && forceplateLabelMap.has(unique_identifier)) {
                    displayLabel = forceplateLabelMap.get(unique_identifier);
                } else if (metric_type === 'testing' && testingLabelMap.has(unique_identifier)) {
                    displayLabel = testingLabelMap.get(unique_identifier);
                }

                groupOptions.push({
                    label: name,
                    displayLabel: displayLabel,
                    value: unique_identifier,
                    key: unique_identifier,
                });
            })
            if (groupOptions.length > 0) {
                let categoryLabel;
                if (metric_type === "gps")
                    categoryLabel = "GPS";
                else {
                    categoryLabel = metric_type.charAt(0).toUpperCase() + metric_type.slice(1).toLowerCase();
                }

                if (categoryLabel !== "Wellness") {
                    optGroupDropdown.push({
                        label: categoryLabel,
                        displayLabel: categoryLabel,
                        key: requirements.key,
                        options: groupOptions,
                    });
                }
            }
        });

        optGroupDropdown.forEach((group) => {
            if (group.label !== "GPS") {
                group.options.sort((a, b) => { return (a.label.toLowerCase()).localeCompare(b.label.toLowerCase()) })
            }
        })

        setMetricDropdown(optGroupDropdown)
    }, [])


    const handleMetricChange = (index: any, metric: any) => {
        selections[index].metric = metric;
        setSelections(JSON.parse(JSON.stringify(selections)));
    };

    const handleAggChange = (index: any, agg: any) => {
        selections[index].agg = agg;
        setSelections(JSON.parse(JSON.stringify(selections)));
    };

    const handleSubmit = () => {
        for (let i = 0; i < selections.length; i++) {
            if (!selections[i].metric) {
                audioMessage.error(`Please select a metric for Metric #${formatNumber(i + 1)}`);
                return;
            }
        }

        setResult && setResult(selections);
        setOpen && setOpen(false);
    };

    const reportConfig = requirements;
    if (!reportConfig) return <p>Invalid report type provided.</p>;

    const get_helper_text = () => {
        if (requirements?.metric_selector_info?.show_helper_text) {
            let i = 1;
            return (
                <Text type="secondary" style={{ marginBottom: "10px" }}>
                    Based on your data stream selections, you need to select {requirements?.metric_selector_info?.count} metrics. <br />
                    <ul>
                        {requirements?.metric_selector_info?.types?.map((type: string) => {
                            let line;
                            if (type === "report-card") {
                                line = <li>One Pager Report Card (20): Metric #{i} - Metric #{i + 19}</li>
                                i += 20;
                            } else if (type === "gps") {
                                line = <li>GPS (4): Metric #{i} - Metric #{i + 3}</li>
                                i += 4;
                            } else if (type === "forceplate") {
                                line = <li>Force Plate (4): Metric #{i} - Metric #{i + 3}</li>
                                i += 4;
                            } else if (type === "testing") {
                                line = <li>Testing(4): Metric #{i} - Metric #{i + 3}</li>
                                i += 4;
                            } else {
                                return line
                            }
                            return line;
                        })}
                    </ul>
                </Text>
            );
        } else {
            return null;
        }

    }

    return (
        <div>
            {get_helper_text()}
            {(presetInfo && presetType) &&
                <>
                    <div style={{ width: '700px', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                        <div style={{ flexGrow: 1 }}>
                            <SelectWithAdd
                                items={presetItems || []}
                                addItem={addPreset}
                                deleteItem={deletePreset}
                                placeholder="Select Preset"
                                setValue={setCurrentSelectedTilePreset}
                                currentSelectedTilePreset={currentSelectedTilePreset}
                                addItemLabel={"Add"}
                                addItemTextboxLabel={"Enter Preset Name"}
                                label={"Would you like to select pre-configured metrics?"}
                            />
                        </div>

                        <div>
                            <Tooltip title={getTooltipText() && getTooltipText()}>
                                <Button type="primary" size="small"
                                    onClick={updatedPreset}
                                    disabled={getPresetSelected()?.creator_id__email !== user?.email}
                                    style={{ width: "100px" }}
                                    className={(getPresetSelected()?.creator_id__email === user?.email && unsavedPresetChanges) ? 'pulse' : ''}
                                >
                                    Save Preset
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                </>
            }
            {selections.map((row: any, idx: number) => (
                <Row key={`${row['metric']}-${idx}`} align="middle">
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                        <Text strong style={{ marginRight: '10px' }}>Metric #{formatNumber(idx + 1)}: </Text>
                        <Select
                            options={metricDropdown}
                            placeholder="Choose Metric"
                            style={{ width: 400, marginRight: "10px" }}
                            onChange={(value) => {
                                setUnsavedPresetChanges(true)
                                handleMetricChange(idx, value)
                            }}
                            value={row['metric']}
                            showSearch
                            fieldNames={{ label: 'displayLabel', value: 'value' }}
                            filterOption={(input, option) => {
                                return option?.displayLabel?.toLowerCase()?.includes(input.toLowerCase()) || false;
                            }
                            }
                        />
                        {
                            showAggs ? (
                                <Segmented
                                    options={[
                                        { label: 'Avg', value: 'avg' },
                                        { label: 'Best', value: 'best' },
                                        { label: 'Sum', value: 'sum' }]}
                                    onChange={(value) => {
                                        setUnsavedPresetChanges(true)
                                        handleAggChange(idx, value);
                                    }}
                                    value={row['agg']}
                                />) : null
                        }
                    </div>
                </Row>
            ))}

            <Button
                type="primary"
                style={{ width: "100%" }}
                onClick={() => handleSubmit()}
            >
                Save Configuration
            </Button>
        </div>
    );
};